import React, { useState, useEffect } from 'react';
import s from './OrderBook.module.sass';

export const OrderBook = () => {
    const [price, setPrice] = useState(0); // начальная цена
    const [buyOrders, setBuyOrders] = useState([]);
    const [sellOrders, setSellOrders] = useState([]);
    const [spread, setSpread] = useState(0);
    const [tradeHistory, setTradeHistory] = useState([]);

    const fetchCurrentPrice = async () => {
        try {
            const response = await fetch('/api/binance/ticker/price?symbol=BNBUSDT');
            const data = await response.json();
            const currentPrice = parseFloat(data.price); // цена BNB в USDT
            setPrice(currentPrice);
            initializeOrders(currentPrice); // Инициализируем заявки и историю на основе текущей цены
        } catch (error) {
            console.error("Ошибка при получении цены BNB:", error);
        }
    };

    const initializeOrders = (currentPrice) => {
        const initialBuyOrders = Array.from({ length: 5 }, (_, index) => ({
            id: `buy-${Date.now()}-${index}`,
            price: (currentPrice - Math.random() * 0.1).toFixed(2),
            targetAmount: Math.random() * 5, // целевое количество
            currentAmount: 0, // текущее заполнение
            fillSpeed: Math.random() * 0.3 + 0.1, // скорость заполнения (0.1-0.4 за шаг)
            fillInterval: Math.random() * 400 + 200, // интервал между шагами (200-600мс)
        }));

        const initialSellOrders = Array.from({ length: 5 }, (_, index) => ({
            id: `sell-${Date.now()}-${index}`,
            price: (currentPrice * 1.01 + Math.random() * 0.1).toFixed(2),
            targetAmount: Math.random() * 5,
            currentAmount: 0,
            fillSpeed: Math.random() * 0.3 + 0.1,
            fillInterval: Math.random() * 400 + 200,
        }));

        const initialTradeHistory = Array.from({ length: 10 }, () => {
            const isBuyTrade = Math.random() > 0.5;
            const tradePrice = isBuyTrade
                ? (currentPrice - Math.random() * 0.1).toFixed(2)
                : (currentPrice * 1.01 + Math.random() * 0.1).toFixed(2);
            return {
                time: new Date().toLocaleTimeString('ms-MY', {
                    timeZone: 'Asia/Kuala_Lumpur',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit',
                    hourCycle: 'h23' // 24-часовой формат
                }),
                price: tradePrice,
                amount: (Math.random() * 5).toFixed(5),
                type: isBuyTrade ? 'buy' : 'sell',
            };
        });

        setBuyOrders(initialBuyOrders);
        setSellOrders(initialSellOrders);
        setSpread((parseFloat(initialSellOrders[0].price) - parseFloat(initialBuyOrders[0].price)).toFixed(2));
        setTradeHistory(initialTradeHistory);

        // Запускаем заполнение ордеров
        initialBuyOrders.forEach(order => startOrderFilling(order, 'buy'));
        initialSellOrders.forEach(order => startOrderFilling(order, 'sell'));
    };

    // Функция для постепенного заполнения ордера
    const startOrderFilling = (order, type) => {
        const fillOrder = () => {
            if (order.currentAmount >= order.targetAmount) {
                // Ордер заполнен на 100%, заменяем его новым через короткое время
                setTimeout(() => {
                    const newOrder = createNewOrder(parseFloat(price));
                    
                    if (type === 'buy') {
                        setBuyOrders(prevOrders => 
                            prevOrders.map(o => o.id === order.id ? newOrder.buyOrder : o)
                        );
                        startOrderFilling(newOrder.buyOrder, 'buy');
                    } else {
                        setSellOrders(prevOrders => 
                            prevOrders.map(o => o.id === order.id ? newOrder.sellOrder : o)
                        );
                        startOrderFilling(newOrder.sellOrder, 'sell');
                    }
                }, Math.random() * 500 + 200); // Замена через 200-700мс после заполнения
                return;
            }

            // Вычисляем следующий шаг заполнения
            const remaining = order.targetAmount - order.currentAmount;
            const step = Math.min(remaining, order.fillSpeed * Math.random() + 0.05); // случайный шаг
            
            order.currentAmount = Math.min(order.currentAmount + step, order.targetAmount);

            // Обновляем состояние
            if (type === 'buy') {
                setBuyOrders(prevOrders => 
                    prevOrders.map(o => o.id === order.id ? { ...o, currentAmount: order.currentAmount } : o)
                );
            } else {
                setSellOrders(prevOrders => 
                    prevOrders.map(o => o.id === order.id ? { ...o, currentAmount: order.currentAmount } : o)
                );
            }

            // Планируем следующий шаг, если ордер еще не заполнен
            if (order.currentAmount < order.targetAmount) {
                setTimeout(fillOrder, order.fillInterval + Math.random() * 200);
            }
        };

        // Начинаем заполнение с небольшой задержкой
        setTimeout(fillOrder, Math.random() * 1000);
    };

    // Функция для создания нового ордера
    const createNewOrder = (currentPrice) => {
        const buyOrder = {
            id: `buy-${Date.now()}-${Math.random()}`,
            price: (currentPrice - Math.random() * 0.1).toFixed(2),
            targetAmount: Math.random() * 5,
            currentAmount: 0,
            fillSpeed: Math.random() * 0.3 + 0.1,
            fillInterval: Math.random() * 400 + 200,
        };
        
        const sellOrder = {
            id: `sell-${Date.now()}-${Math.random()}`,
            price: (currentPrice * 1.01 + Math.random() * 0.1).toFixed(2),
            targetAmount: Math.random() * 5,
            currentAmount: 0,
            fillSpeed: Math.random() * 0.3 + 0.1,
            fillInterval: Math.random() * 400 + 200,
        };

        return { buyOrder, sellOrder };
    };

    useEffect(() => {
        fetchCurrentPrice();
        const intervalPrice = setInterval(fetchCurrentPrice, 5000);

        return () => clearInterval(intervalPrice);
    }, []);

    useEffect(() => {
        // Функция для генерации случайного интервала с приоритетом на 500-1000 мс
        const getRandomInterval = () => {
            return Math.random() < 0.7 
                ? Math.floor(Math.random() * (1000 - 500 + 1)) + 500 // 70% шанс на интервал от 500 до 1000 мс
                : Math.floor(Math.random() * (500 - 50 + 1)) + 50;   // 30% шанс на интервал от 50 до 500 мс
        };

        const generateOrders = () => {
            const currentPrice = parseFloat(price);
            const buyOrder = {
                id: `buy-${Date.now()}`,
                price: (currentPrice - Math.random() * 0.1).toFixed(2),
                targetAmount: Math.random() * 5,
                currentAmount: 0,
                fillSpeed: Math.random() * 0.3 + 0.1,
                fillInterval: Math.random() * 400 + 200,
            };
            const sellOrder = {
                id: `sell-${Date.now()}`,
                price: (currentPrice * 1.01 + Math.random() * 0.1).toFixed(2),
                targetAmount: Math.random() * 5,
                currentAmount: 0,
                fillSpeed: Math.random() * 0.3 + 0.1,
                fillInterval: Math.random() * 400 + 200,
            };

            setBuyOrders((prevOrders) => {
                const updatedBuyOrders = [buyOrder, ...prevOrders];
                return updatedBuyOrders.length > 5 ? updatedBuyOrders.slice(0, 5) : updatedBuyOrders;
            });

            setSellOrders((prevOrders) => {
                const updatedSellOrders = [sellOrder, ...prevOrders];
                return updatedSellOrders.length > 5 ? updatedSellOrders.slice(0, 5) : updatedSellOrders;
            });

            // Запускаем заполнение новых ордеров
            startOrderFilling(buyOrder, 'buy');
            startOrderFilling(sellOrder, 'sell');

            setSpread((parseFloat(sellOrder.price) - parseFloat(buyOrder.price)).toFixed(2));

            const isBuyTrade = Math.random() > 0.5;
            const tradePrice = isBuyTrade ? buyOrder.price : sellOrder.price;

            const currentTime = new Date().toLocaleTimeString('ms-MY', {
                timeZone: 'Asia/Kuala_Lumpur',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hourCycle: 'h23' // 24-часовой формат
            });

            const newTrade = {
                                  time: currentTime,
                price: tradePrice,
                amount: (Math.random() * 5).toFixed(5),
                type: isBuyTrade ? 'buy' : 'sell',
            };

            setTradeHistory((prevHistory) => {
                const updatedHistory = [newTrade, ...prevHistory];
                return updatedHistory.length > 10 ? updatedHistory.slice(0, 10) : updatedHistory;
            });

            const randomInterval = getRandomInterval();
            setTimeout(generateOrders, randomInterval);
        };

        const initialInterval = getRandomInterval();
        const timeoutId = setTimeout(generateOrders, initialInterval);

        return () => clearTimeout(timeoutId);
    }, [price]);

    return (
        <div className={s['order-book-container']}>
            <div className={s['order-book']}>
                <h2>Order History</h2>
                <table>
                    <thead>
                        <tr>
                            <th>Price (USD)</th>
                            <th>Amount (BNB)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sellOrders.map((order, index) => (
                            <tr key={order.id || `sell-${index}`}>
                                <td className={`${s.price} ${s.sell}`} style={{ position: 'relative' }}>
                                    <div className={s.sellBar} style={{ width: `${order.currentAmount * 20}%` }}></div>
                                    {order.price}
                                </td>
                                <td>{order.currentAmount.toFixed(5)}</td>
                            </tr>
                        ))}
                        <tr>
                            <td colSpan="2" className={s.spreadRow}>Propagación {spread}</td>
                        </tr>
                        {buyOrders.map((order, index) => (
                            <tr key={order.id || `buy-${index}`}>
                                <td className={`${s.price} ${s.buy}`} style={{ position: 'relative' }}>
                                    <div className={s.buyBar} style={{ width: `${order.currentAmount * 20}%` }}></div>
                                    {order.price}
                                </td>
                                <td>{order.currentAmount.toFixed(5)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className={s['trade-history']}>
                <h2>Trading History</h2>
                <table>
                    <thead>
                        <tr>
                                                    <th>Time</th>
                        <th>Price</th>
                            <th>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tradeHistory.map((trade, index) => (
                            <tr key={index}>
                                <td>{trade.time}</td>
                                <td className={`${s.price} ${s[trade.type]}`}>
                                    {trade.price}
                                </td>
                                <td>{trade.amount}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
